/**
 * Generate a shareable card image (PNG) from a fortune result.
 * Uses HTML5 Canvas — no external dependencies.
 */

export async function generateShareCard({ title, snippet, name = "FalZilla", subtitle = "" }) {
  const W = 1080, H = 1920;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#0B0E14"); bg.addColorStop(0.5, "#161832"); bg.addColorStop(1, "#0B0E14");
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

  const glow = ctx.createRadialGradient(W / 2, 250, 50, W / 2, 250, 700);
  glow.addColorStop(0, "rgba(212,175,55,0.35)"); glow.addColorStop(1, "rgba(212,175,55,0)");
  ctx.fillStyle = glow; ctx.fillRect(0, 0, W, H);

  ctx.fillStyle = "rgba(255,255,255,0.7)";
  for (let i = 0; i < 100; i++) {
    const x = Math.random() * W, y = Math.random() * H, r = Math.random() * 2 + 0.5;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
  }
  ctx.fillStyle = "rgba(212,175,55,0.8)";
  for (let i = 0; i < 20; i++) {
    const x = Math.random() * W, y = Math.random() * H, r = Math.random() * 3 + 1;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
  }

  ctx.fillStyle = "#D4AF37";
  ctx.font = "600 36px 'Manrope', system-ui";
  ctx.textAlign = "center";
  ctx.fillText("✧  " + name.toUpperCase() + "  ✧", W / 2, 180);

  ctx.fillStyle = "#FDFBF7";
  ctx.font = "italic 700 96px 'Cormorant Garamond', serif";
  const titleWords = title.split(" ");
  let line = "", y = 360;
  for (const w of titleWords) {
    if (ctx.measureText(line + w).width > W - 200) { ctx.fillText(line.trim(), W / 2, y); y += 110; line = ""; }
    line += w + " ";
  }
  if (line) ctx.fillText(line.trim(), W / 2, y);

  const cardY = y + 100, cardH = 900;
  ctx.fillStyle = "rgba(255,255,255,0.04)";
  ctx.strokeStyle = "rgba(212,175,55,0.5)";
  ctx.lineWidth = 2;
  roundRect(ctx, 80, cardY, W - 160, cardH, 40);
  ctx.fill(); ctx.stroke();

  ctx.fillStyle = "rgba(253,251,247,0.92)";
  ctx.font = "500 44px 'Manrope', system-ui";
  ctx.textAlign = "left";
  const paddingX = 140, maxW = W - paddingX * 2, lineHeight = 66;
  const lines = wrapText(ctx, snippet, maxW);
  let ty = cardY + 100;
  const maxLines = Math.floor((cardH - 200) / lineHeight);
  for (let i = 0; i < Math.min(lines.length, maxLines); i++) { ctx.fillText(lines[i], paddingX, ty); ty += lineHeight; }

  ctx.fillStyle = "rgba(212,175,55,0.85)";
  ctx.font = "italic 40px 'Cormorant Garamond', serif";
  ctx.textAlign = "center";
  ctx.fillText(subtitle || "— falzilla.com —", W / 2, H - 140);
  ctx.fillStyle = "rgba(253,251,247,0.35)";
  ctx.font = "24px 'Manrope', system-ui";
  ctx.fillText("Eğlence amaçlıdır", W / 2, H - 80);

  return canvas.toDataURL("image/png");
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapText(ctx, text, maxWidth) {
  const words = text.replace(/\s+/g, " ").split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth) {
      if (line) lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/**
 * Extract the most impactful ~2-3 sentences from a fortune text.
 */
export function extractSnippet(text, maxChars = 320) {
  if (!text) return "";
  const cleaned = text
    .replace(/^#+\s+/gm, "")
    .replace(/\*\*/g, "")
    .replace(/\*/g, "")
    .replace(/^\d+\)\s*/gm, "")
    .replace(/^\s*[-•]\s*/gm, "")
    .replace(/\s+/g, " ")
    .trim();

  const sentences = cleaned.split(/(?<=[.!?…])\s+/).filter((s) => s.trim().length > 25);
  if (sentences.length === 0) return cleaned.slice(0, maxChars);

  const KEYWORDS = [
    "aşk", "kısmet", "umut", "güç", "işaret", "yakın", "kalp", "bereket", "şans",
    "güneş", "yıldız", "iyilik", "sevgi", "başarı", "huzur", "yolun", "kader",
    "yeni bir", "önünde", "gelecek", "içinde", "duygu", "bir kapı", "ışık",
  ];
  const scored = sentences.map((s, idx) => {
    const lower = s.toLowerCase();
    let score = 0;
    for (const k of KEYWORDS) if (lower.includes(k)) score += 2;
    const len = s.length;
    if (len >= 40 && len <= 160) score += 3;
    else if (len < 30 || len > 220) score -= 2;
    score += Math.max(0, 3 - Math.floor(idx / 3));
    if (/^\d+\.?$/.test(s.trim())) score -= 5;
    return { s: s.trim(), score, idx };
  });
  scored.sort((a, b) => b.score - a.score);

  const picked = [];
  let total = 0;
  const seenIdx = new Set();
  for (const item of scored) {
    if (seenIdx.has(item.idx)) continue;
    if (total + item.s.length > maxChars) continue;
    picked.push(item);
    seenIdx.add(item.idx);
    total += item.s.length + 2;
    if (picked.length >= 3) break;
  }
  picked.sort((a, b) => a.idx - b.idx);
  const out = picked.map((p) => p.s).join(" ");
  return out || cleaned.slice(0, maxChars);
}

export function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
